import { Box, Paper, Typography } from "@mui/material";
import { NotificationsNoneOutlined } from "@mui/icons-material";
import { Button, Callout, CardPreOferta, PageHeader } from "@components/ui_v2";
import { formatMXN } from "@components/factoraje";

/** Datos de ejemplo del diseño (`S_ExpAutorizado` en modo factoraje de Flux); aún no se leen del store. */
const LINEA_AUTORIZADA = 3100000;
const CONDICIONES = { aforo: 90, tasa: 21.6, comision: 1.2, plazoMax: 120 };
const EMPRESAS_AUTORIZADAS = [
  { nombre: "Refacciones Vega SA", monto: 1150000 },
  { nombre: "Chedraui", monto: 850000 },
  { nombre: "Grupo Bimbo", monto: 550000 },
  { nombre: "Cadena Comercial OXXO", monto: 550000 },
];
const VIGENCIA_DIAS = 15;

/** `.card-title` / `.card-sub` del diseño; en esta pantalla el subtítulo baja a 12.5px. */
const CardTitulo = ({ title, sub, mb }: { title: string; sub: string; mb: string }) => (
  <>
    <Typography sx={{ fontSize: 17, fontWeight: 600, letterSpacing: "-0.015em", color: "primary.dark", mb: "4px" }}>
      {title}
    </Typography>
    <Typography sx={{ fontSize: 12.5, color: "tokens.textTertiary", lineHeight: 1.55, mb }}>{sub}</Typography>
  </>
);

interface NextStepProps {
  n: number;
  title: string;
  sub: string;
}

/** `NextStep` del diseño: mismo spec que en `ExpedienteCompletado`. */
const NextStep = ({ n, title, sub }: NextStepProps) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: "14px" }}>
    <Box
      sx={{
        flexShrink: 0,
        width: 28,
        height: 28,
        borderRadius: "8px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "tokens.pageBg",
      }}
    >
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "primary.main" }}>{n}</Typography>
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "primary.dark", lineHeight: 1.35 }}>{title}</Typography>
      <Typography sx={{ fontSize: 12.5, color: "tokens.textTertiary", lineHeight: 1.5, mt: "2px" }}>{sub}</Typography>
    </Box>
  </Box>
);

/**
 * Expediente autorizado para Mercader PM y PFAE: la línea autorizada, su reparto por empresa y los pasos
 * para activarla. Solo maquetado.
 */
export const ExpedienteAutorizadoMercader = () => (
  <Box>
    <PageHeader
      eyebrow="Formalización · Expediente autorizado"
      title="¡Tu expediente está autorizado!"
      subtitle={
        <>
          Revisamos toda tu información y documentos. La <strong>ratificación crediticia y legal</strong> está lista. El
          siguiente paso es firmar tu contrato de factoraje y activar la línea.
        </>
      }
    />

    <CardPreOferta
      eyebrow="Línea de factoraje autorizada"
      badge="Autorizada"
      amount={formatMXN(LINEA_AUTORIZADA)}
      amountCaption="MXN revolventes"
      sub=""
      items={[
        { label: "Aforo", value: `${CONDICIONES.aforo}%` },
        { label: "Tasa anual", value: `${CONDICIONES.tasa}%` },
        { label: "Comisión por operación", value: `${CONDICIONES.comision}%` },
        { label: "Plazo máximo de factura", value: `${CONDICIONES.plazoMax} días` },
      ]}
    />

    <Paper variant="lendia_paper" sx={{ mt: "18px", p: "22px" }}>
      <CardTitulo
        title="Empresas autorizadas para factorar"
        sub="Así quedó repartida tu línea. Puedes redistribuirla desde tu panel cuando la línea esté activa."
        mb="14px"
      />
      {/* `.fxu-rangos`: chip en el color de acento de la marca, nombre y monto asignado. */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: "8px 20px" }}>
        {EMPRESAS_AUTORIZADAS.map((empresa) => (
          <Box key={empresa.nombre} sx={{ display: "grid", gridTemplateColumns: "4px 1fr", gap: "10px" }}>
            <Box sx={{ borderRadius: "3px", minHeight: 26, bgcolor: "primary.accent" }} />
            <Box>
              <Typography
                sx={{
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: "text.primary",
                  lineHeight: 1.3,
                  overflowWrap: "anywhere",
                }}
              >
                {empresa.nombre}
              </Typography>
              <Typography
                sx={{ fontSize: 11.5, color: "tokens.textTertiary", fontVariantNumeric: "tabular-nums", mt: "1px" }}
              >
                {formatMXN(empresa.monto)}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Paper>

    <Paper variant="lendia_paper" sx={{ mt: "18px", p: "22px" }}>
      <CardTitulo title="Lo que sigue" sub="Tres pasos para activar tu línea." mb="18px" />
      <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        <NextStep
          n={1}
          title="Generación de contratos"
          sub="Preparamos el contrato de factoraje y sus anexos con los datos validados de tu expediente."
        />
        <NextStep
          n={2}
          title="Revisión de condiciones"
          sub="Confirmas monto de la línea, aforo, tasa y comisiones antes de firmar."
        />
        <NextStep
          n={3}
          title="Firma electrónica"
          sub="Firmas los documentos con tu FIEL. Al firmar, tu línea queda activa para descontar facturas."
        />
      </Box>
      <Box sx={{ mt: "18px" }}>
        <Callout tone="info" icon={<NotificationsNoneOutlined sx={{ fontSize: 14 }} />}>
          Te enviamos una copia de este resumen por correo y WhatsApp. La autorización es vigente por{" "}
          <strong>{VIGENCIA_DIAS} días naturales</strong>.
        </Callout>
      </Box>
    </Paper>

    {/* `.actions.actions-end`; sin navegación todavía: solo maquetado. */}
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        gap: "10px",
        mt: 4,
        pt: "20px",
        borderTop: "1px solid",
        borderColor: "divider",
      }}
    >
      <Button tone="outline">Descargar resumen</Button>
      <Button tone="primary" withArrow>
        Continuar
      </Button>
    </Box>
  </Box>
);
